import { Button, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { HelpCircle, Loader2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  ProjectDetail,
  ProjectListItem,
} from "@/modules/management/project/domain/types";
import { managementProjectApi } from "../api/client";
import { useProjectById } from "../api/queries";
import {
  ProjectFormTour,
  useProjectFormTour,
} from "../tour/ProjectFormTour";
import type { ProjectFormValues } from "../ui/ProjectForm";
import { ProjectForm } from "../ui/ProjectForm";

type ProjectFormPageProps = {
  mode: "create" | "edit";
};

const emptyFormValues: ProjectFormValues = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
  responsibleUserId: "",
  level: "organization",
  positionId: null,
  departmentId: null,
  phases: [],
};

function projectDetailToFormValues(detail: ProjectDetail): ProjectFormValues {
  return {
    name: detail.name,
    description: detail.description ?? "",
    startDate: detail.startDate,
    endDate: detail.endDate,
    responsibleUserId: detail.responsibleUserId ?? "",
    level: detail.level ?? "organization",
    positionId: detail.positionId ?? null,
    departmentId: detail.departmentId ?? null,
    phases: (detail.phases ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      dueDate: p.dueDate ?? undefined,
      assigneeId: p.assigneeId ?? "",
    })),
  };
}

function projectListItemToFormValues(
  project: ProjectListItem,
): ProjectFormValues {
  return {
    name: project.name,
    description: project.description ?? "",
    startDate: project.startDate,
    endDate: project.endDate,
    responsibleUserId: project.responsibleUserId ?? "",
    level: project.level ?? "organization",
    positionId: project.positionId ?? null,
    departmentId: project.departmentId ?? null,
    phases: [],
  };
}

export function ProjectFormPage({ mode }: ProjectFormPageProps) {
  const params = useParams({ strict: false }) as { id?: string };
  const tour = useProjectFormTour();
  const locationState = useRouterState({ select: (s) => s.location.state });
  const projectFromState = (
    locationState as { project?: ProjectListItem } | undefined
  )?.project;

  const projectQuery = useProjectById(mode === "edit" ? params.id : undefined);

  const initialValues = useMemo((): ProjectFormValues => {
    if (mode === "edit" && projectQuery.data) {
      return projectDetailToFormValues(projectQuery.data);
    }
    if (
      mode === "edit" &&
      projectFromState &&
      (projectQuery.isError || !params.id)
    ) {
      return projectListItemToFormValues(projectFromState);
    }
    return emptyFormValues;
  }, [
    mode,
    projectQuery.data,
    projectQuery.isError,
    projectFromState,
    params.id,
  ]);

  const nav = useNavigate({ from: "/app/management/projects" });
  const queryClient = useQueryClient();
  const formRef = useRef<{ submit: () => void } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const listSearch = {
    offset: 0,
    limit: 20,
    sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
    status: undefined as string | undefined,
    level: undefined as string | undefined,
    responsibleUserId: undefined as string | undefined,
    startDateFrom: undefined as string | undefined,
    startDateTo: undefined as string | undefined,
    endDateFrom: undefined as string | undefined,
    endDateTo: undefined as string | undefined,
    search: undefined as string | undefined,
  };

  const handleCancel = () => {
    nav({ to: "/app/management/projects", search: listSearch });
  };

  const handleSubmit = async (values: ProjectFormValues) => {
    setIsSubmitting(true);
    const body = managementProjectApi.formValuesToBody(values);
    try {
      if (mode === "create") {
        await managementProjectApi.create(body);
        toast.success("ສ້າງໂຄງການສຳເລັດ");
      } else {
        const id = params.id;
        if (!id) {
          toast.error("ບໍ່ພົບ ID ໂຄງການ");
          setIsSubmitting(false);
          return;
        }
        await managementProjectApi.update(id, body);
        toast.success("ບັນທຶກການປ່ຽນແປງສຳເລັດ");
      }
      await queryClient.invalidateQueries({
        queryKey: ["management", "projects"],
      });
      nav({ to: "/app/management/projects", search: listSearch });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໝ່");
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = mode === "create" ? "ສ້າງໂຄງການ" : "ແກ້ໄຂໂຄງການ";

  const isLoadingEdit = mode === "edit" && params.id && projectQuery.isLoading;
  const isNotFound =
    mode === "edit" && params.id && projectQuery.isError && !projectFromState;

  if (isLoadingEdit) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດໂຄງການ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (isNotFound) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <p className="text-muted-foreground text-sm">ບໍ່ພົບໂຄງການ</p>
            <Button variant="outline" onClick={handleCancel}>
              ກັບໄປລາຍການໂຄງການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <ProjectFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6 flex flex-col gap-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h1 className="font-semibold text-2xl">{title}</h1>
                <p className="text-muted-foreground text-sm">
                  ກຳນົດຂອບເຂດ ເວລາດຳເນີນງານ ແລະ ຂັ້ນຕອນໂຄງການ
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດລອງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <div className="flex gap-2" data-tourid="form-submit">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                onClick={() => formRef.current?.submit()}
                disabled={isSubmitting}
              >
                {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກໂຄງການ"}
              </Button>
            </div>
          </div>
        </div>

        <ProjectForm
          key={
            mode === "edit" && projectQuery.data
              ? projectQuery.data.id
              : (params.id ?? "new")
          }
          defaultValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          mode={mode}
          formRef={formRef}
        />
      </Main>
    </>
  );
}
